// File: src/components/Blog/BlogDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { db } from "../../modules/auth/firebaseConfig";
import { doc, getDoc, updateDoc, increment } from "firebase/firestore";
import BlogHeader from "./BlogHeader";

import FooterLinks from "../../components/LandingPage/FooterLinks";
import PublicSocialShare from "./PublicSocialShare";
import SeoMeta from "../SEO/SeoMeta";

// ---------- helpers ----------
const isImage = (t = "") => t.startsWith("image/");
const isVideo = (t = "") => t.startsWith("video/");
const isPdf = (t = "") => t === "application/pdf" || /\.pdf(\?|$)/i.test(t);

// Use 4:5 on mobile (1080x1350) → switch to 16:9 from sm+
// If you prefer 9:16 on mobile, change MOBILE_ASPECT to 'aspect-[9/16]'.
const MOBILE_ASPECT = "aspect-[4/5]";

function MediaFrame({ children }) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-900 bg-black/40">
      {/* portrait on mobile, 16:9 on sm+ */}
      <div className={`w-full ${MOBILE_ASPECT} sm:aspect-video`}>
        <div className="h-full w-full">{children}</div>
      </div>
    </div>
  );
}

const FitImage = ({ src, alt }) => (
  <MediaFrame>
    <img
      src={src}
      alt={alt || ""}
      className="block h-full max-h-[90vh] w-full object-contain"
      loading="lazy"
    />
  </MediaFrame>
);

const FitVideo = ({ src, poster }) => (
  <MediaFrame>
    <video
      controls
      playsInline
      src={src}
      poster={poster}
      className="block h-full max-h-[90vh] w-full bg-black object-contain"
    />
  </MediaFrame>
);

export default function BlogDetail() {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const ref = doc(db, "post", postId);
        const snap = await getDoc(ref);
        if (!mounted) return;
        if (snap.exists()) {
          const data = { id: snap.id, ...snap.data() };
          setPost(data);
          // fire-and-forget
          try {
            await updateDoc(ref, { views: increment(1) });
          } catch {}
        } else {
          setPost(null);
        }
      } catch (e) {
        console.error("Error fetching blog post:", e);
        setPost(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [postId]);

  if (loading)
    return <div className="py-10 text-center text-white">Loading...</div>;
  if (!post)
    return <div className="py-10 text-center text-white">Post not found.</div>;

  const {
    caption,
    description,
    descriptionSections,
    fileUrl,
    fileType,
    keywords,
    createdAt,
    thumbnailUrl,
  } = post;

  const createdAtDate =
    createdAt?.toDate?.() ??
    (createdAt?.seconds ? new Date(createdAt.seconds * 1000) : null);

  const formattedDate = createdAtDate
    ? createdAtDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";

  // SEO picks
  const leadMedia = (() => {
    if (thumbnailUrl) return thumbnailUrl;
    if (Array.isArray(descriptionSections)) {
      const firstWithMedia = descriptionSections.find((s) => s?.media?.url);
      if (firstWithMedia?.media?.url) return firstWithMedia.media.url;
    }
    return fileUrl || "";
  })();

  const seoDescription = (() => {
    if (description && description.trim()) return description;
    if (Array.isArray(descriptionSections) && descriptionSections.length) {
      const joined = descriptionSections
        .map((s) => s?.text || "")
        .join(" ")
        .trim();
      return joined.slice(0, 240) || caption || "";
    }
    return caption || "";
  })();

  return (
    <div className="min-h-screen border-b bg-black text-white">
      <BlogHeader />

      <SeoMeta
        title={`${caption} | Million2Trillion Blog`}
        description={seoDescription}
        url={`https://million2trillion.com/blog/${postId}`}
        image={leadMedia}
        keywords={keywords || ""}
        isBlogPost={true}
        datePublished={createdAtDate ? createdAtDate.toISOString() : undefined}
      />

      <main className="mx-auto max-w-3xl px-4 py-16">
        <h1 className="mb-2 text-3xl font-bold">{caption}</h1>

        {(formattedDate || keywords) && (
          <div className="mb-6 flex flex-wrap gap-x-4 gap-y-2 text-sm text-gray-400">
            {formattedDate && <span>Posted on: {formattedDate}</span>}
          </div>
        )}

        {/* Legacy single-file display */}
        {!Array.isArray(descriptionSections) && fileUrl && (
          <div className="mb-6">
            {isImage(fileType) && (
              <FitImage src={thumbnailUrl || fileUrl} alt="" />
            )}
            {isVideo(fileType) && (
              <FitVideo src={fileUrl} poster={thumbnailUrl} />
            )}
            {isPdf(fileType) && (
              <div className="rounded-lg border border-gray-900 p-4">
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 underline"
                >
                  📄 View PDF
                </a>
              </div>
            )}
          </div>
        )}

        {/* Multi-section renderer */}
        {Array.isArray(descriptionSections) &&
        descriptionSections.length > 0 ? (
          <article className="space-y-8">
            {descriptionSections.map((sec) => {
              const media = sec?.media;
              const type = media?.type || "";
              return (
                <section
                  key={sec.id || `${postId}-${Math.random()}`}
                  className="space-y-4 rounded-lg border border-gray-900 p-4"
                >
                  {media?.url && (
                    <>
                      {isImage(type) && (
                        <FitImage src={media.url} alt={media.name || ""} />
                      )}
                      {isVideo(type) && <FitVideo src={media.url} />}
                      {isPdf(type) && (
                        <a
                          href={media.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 underline"
                        >
                          📄 {media.name || "View PDF"}
                        </a>
                      )}
                      {!isImage(type) && !isVideo(type) && !isPdf(type) && (
                        <a
                          href={media.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="break-all text-blue-400 underline"
                        >
                          {media.name || media.url}
                        </a>
                      )}
                    </>
                  )}
                  {sec?.text && (
                    <p className="whitespace-pre-line text-lg leading-relaxed text-gray-200">
                      {sec.text}
                    </p>
                  )}
                </section>
              );
            })}
          </article>
        ) : (
          description && (
            <p className="mb-12 mt-4 whitespace-pre-line text-lg text-gray-300">
              {description}
            </p>
          )
        )}

        <div className="mb-36 mt-6">
          <PublicSocialShare postId={postId} caption={caption} />
        </div>
      </main>
      <FooterLinks />
    </div>
  );
}
